import React, { useMemo, useRef, useState } from "react";
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  LinkOutlined,
  PaperClipOutlined,
  RobotOutlined,
  SendOutlined,
  SlackOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Avatar,
  Badge,
  Button,
  DatePicker,
  Descriptions,
  Divider,
  Drawer,
  Empty,
  Input,
  List,
  Mentions,
  message,
  Popconfirm,
  Select,
  Spin,
  Tag,
  Typography,
  Upload,
} from "antd";
import dayjs from "dayjs";
import ReactMarkdown, { type Components } from "react-markdown";
import { Link } from "react-router-dom";

import {
  APP_RELEASE_PLATFORM_LABELS,
  PermissionKey,
  PermissionScope,
  SupportRequestCommentAuthorType,
  type AppReleasePlatform,
  type SupportRequestAttachment,
  type SupportRequestCommentMetadata,
  type SupportRequestFixedInVersion,
  type SupportRequestMetadata,
  type SupportRequestStatus,
  type SupportRequestTriage,
} from "@fitness-app/data-models";

import { HeicImage } from "~/components/HeicImage/HeicImage";
import { usePermission } from "~/hooks/usePermission";
import { useUserRole } from "~/hooks/trainer/useUserRole";
import { useTagOptions } from "~/hooks/useTagOptions";
import { useAppReleases } from "../hooks/useAppReleases";
import {
  useAddSupportComment,
  useDeleteSupportComment,
  useSupportComments,
  useUpdateSupportComment,
  type SupportCommentWithAuthor,
} from "../hooks/useSupportComments";
import {
  useCoalescedSupportRequestFieldUpdate,
  useDeleteSupportRequest,
  useResolveTriage,
  useSyncRequestToLinear,
  useUpdateSupportRequest,
  useUpdateSupportStatus,
} from "../hooks/useSupportApi";
import { useSupportStaff } from "../hooks/useSupportStaff";
import { useTraineeSearch } from "../hooks/useTraineeSearch";
import { uploadSupportAttachment } from "../supportAttachments";
import { StatusChangeCommentModal, type PendingStatusChange } from "./StatusChangeCommentModal";
import { isImageAttachment, SupportAttachmentList } from "./SupportAttachmentList";
import {
  BOARD_COLUMNS,
  CLOSED_SUPPORT_STATUSES,
  SOURCE_LABELS,
  STATUS_BADGE_COLORS,
  STATUS_COLORS,
  STATUS_LABELS,
  formatPersonName,
  isKnownReleasePlatform,
  parseFixedInVersions,
  releaseLabel,
  type SupportRequestWithRelations,
} from "../supportConstants";

interface SupportRequestDrawerProps {
  request: SupportRequestWithRelations | null;
  onClose: () => void;
}

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Client-side mirror of the add-comment endpoint's attachments cap.
const MAX_COMMENT_ATTACHMENTS = 10;

// "Anna Kowalska (anna@example.com)" — the client picker and the read-only link
// name the same person the same way. Not formatPersonName(): that falls back to
// the email, which would print it twice here.
const traineeName = (trainee: NonNullable<SupportRequestWithRelations["trainee"]>) =>
  [trainee.firstName, trainee.lastName].filter(Boolean).join(" ") || "Klient";
const traineeLabel = (trainee: NonNullable<SupportRequestWithRelations["trainee"]>) =>
  `${traineeName(trainee)} (${trainee.email})`;

// Select values are "platform:version" — split on the FIRST colon so a version
// string containing a colon still round-trips.
const encodeRelease = (entry: SupportRequestFixedInVersion) => `${entry.platform}:${entry.version}`;
const decodeRelease = (value: string): SupportRequestFixedInVersion => {
  const separator = value.indexOf(":");
  return {
    platform: value.slice(0, separator) as AppReleasePlatform,
    version: value.slice(separator + 1),
  };
};

// Highlights "@Full Name" mention tokens inside already-rendered markdown text. Only string
// children are split; nested elements (e.g. **bold**) are returned untouched and get highlighted
// by their own renderer, which also runs this helper.
const highlightMentions = (children: React.ReactNode, pattern: RegExp | null, tokens: Set<string>): React.ReactNode => {
  if (!pattern) {
    return children;
  }
  return React.Children.map(children, (child) => {
    if (typeof child !== "string") {
      return child;
    }
    return child.split(pattern).map((part, index) =>
      tokens.has(part) ? (
        <span key={index} className="rounded bg-blue-50 px-0.5 font-medium text-blue-600">
          {part}
        </span>
      ) : (
        <React.Fragment key={index}>{part}</React.Fragment>
      ),
    );
  });
};

// Agent-written comments (diagnoses) are authored in Markdown, so they render with react-markdown;
// `whitespace-pre-wrap` on paragraphs keeps soft line breaks. Human comments stay verbatim plain
// text — markdown would mangle pasted paths, underscores, or list-like lines. Both paths highlight
// the "@Full Name" mentions whose userIds ride along in `mentions`.
const CommentBody: React.FC<{ comment: SupportCommentWithAuthor; mentionNames: string[] }> = ({
  comment,
  mentionNames,
}) => {
  const { pattern, tokens } = useMemo(() => {
    if (!mentionNames.length) {
      return { pattern: null as RegExp | null, tokens: new Set<string>() };
    }
    // Longest names first — otherwise "@Jan" would split "@Jan Kowalski" mid-token.
    const sortedNames = [...mentionNames].sort((a, b) => b.length - a.length);
    return {
      pattern: new RegExp(`(${sortedNames.map((name) => escapeRegExp(`@${name}`)).join("|")})`, "g"),
      tokens: new Set(sortedNames.map((name) => `@${name}`)),
    };
  }, [mentionNames]);

  const components: Components = useMemo(
    () => ({
      p: ({ children }) => (
        <p className="mb-2 whitespace-pre-wrap last:mb-0">{highlightMentions(children, pattern, tokens)}</p>
      ),
      strong: ({ children }) => (
        <strong className="font-semibold">{highlightMentions(children, pattern, tokens)}</strong>
      ),
      em: ({ children }) => <em>{highlightMentions(children, pattern, tokens)}</em>,
      ul: ({ children }) => <ul className="mb-2 list-disc pl-5 last:mb-0">{children}</ul>,
      ol: ({ children }) => <ol className="mb-2 list-decimal pl-5 last:mb-0">{children}</ol>,
      li: ({ children }) => <li className="mb-0.5">{highlightMentions(children, pattern, tokens)}</li>,
      a: ({ children, ...props }) => (
        <a className="text-blue-600 underline" target="_blank" rel="noopener noreferrer" {...props}>
          {children}
        </a>
      ),
      code: ({ children, ...props }) => (
        <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-[0.85em]" {...props}>
          {children}
        </code>
      ),
      pre: ({ children }) => (
        <pre className="mb-2 overflow-x-auto rounded bg-gray-100 p-2 text-sm last:mb-0">{children}</pre>
      ),
      // Headings in a short comment read better as emphasized lines than page-sized titles.
      h1: ({ children }) => (
        <p className="mb-2 font-semibold last:mb-0">{highlightMentions(children, pattern, tokens)}</p>
      ),
      h2: ({ children }) => (
        <p className="mb-2 font-semibold last:mb-0">{highlightMentions(children, pattern, tokens)}</p>
      ),
      h3: ({ children }) => (
        <p className="mb-2 font-semibold last:mb-0">{highlightMentions(children, pattern, tokens)}</p>
      ),
    }),
    [pattern, tokens],
  );

  if (comment.authorType !== SupportRequestCommentAuthorType.Agent) {
    // Human-authored comment: render verbatim, only highlighting @mentions.
    return (
      <Typography.Paragraph className="mb-0 whitespace-pre-wrap">
        {highlightMentions(comment.body, pattern, tokens)}
      </Typography.Paragraph>
    );
  }

  return (
    <div className="mb-0 break-words text-sm leading-[22px] text-gray-800">
      <ReactMarkdown components={components}>{comment.body}</ReactMarkdown>
    </div>
  );
};

// antd Mentions is a bare textarea, so mention tokens cannot be styled inline. This
// backdrop sits UNDER the (transparent-background) textarea and paints a pill behind
// each "@Full Name" token — its own text is fully transparent, so the real text and
// caret render on top, pixel-aligned via matching font metrics (14px / 22px / 4x11px
// padding = antd textarea defaults). autoSize on the Mentions keeps it from scrolling
// out of sync for typical comment lengths.
const MentionsHighlightOverlay: React.FC<{ body: string; names: string[] }> = ({ body, names }) => {
  if (!names.length || !body) {
    return null;
  }

  const sortedNames = [...names].sort((a, b) => b.length - a.length);
  const pattern = new RegExp(`(${sortedNames.map((name) => escapeRegExp(`@${name}`)).join("|")})`, "g");
  const tokens = new Set(sortedNames.map((name) => `@${name}`));

  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words rounded-lg border border-transparent px-[11px] py-[4px] text-sm leading-[22px] text-transparent"
    >
      {body.split(pattern).map((part, index) =>
        tokens.has(part) ? (
          <span key={index} className="rounded bg-blue-100">
            {part}
          </span>
        ) : (
          <React.Fragment key={index}>{part}</React.Fragment>
        ),
      )}
    </div>
  );
};

export const SupportRequestDrawer: React.FC<SupportRequestDrawerProps> = ({ request, onClose }) => {
  const { comments, isLoading: commentsLoading } = useSupportComments(request?.id || null);
  const addComment = useAddSupportComment(request?.id || null);
  const updateComment = useUpdateSupportComment(request?.id || null);
  const deleteComment = useDeleteSupportComment(request?.id || null);
  const updateStatus = useUpdateSupportStatus();
  const updateRequest = useUpdateSupportRequest();
  const { queueFieldUpdate } = useCoalescedSupportRequestFieldUpdate();
  const resolveTriage = useResolveTriage();
  const { userId } = useUserRole();
  const canManageSupport = usePermission(PermissionKey.SUPPORT_MANAGE);
  const canManageTeamPermissions = usePermission(PermissionKey.TEAM_PERMISSIONS_MANAGE);
  // Relinking the client moves the ticket between visibility scopes, so it belongs
  // to the roles that curate the whole board — owner, administration, support
  // (TRE-2005). Mirrors the endpoint's own gate.
  const canEditClient = usePermission(PermissionKey.SUPPORT_MANAGE, PermissionScope.ALL);
  const deleteRequest = useDeleteSupportRequest();
  // Mirrors the delete endpoint: a related support manager must also be the
  // reporter or hold the non-overridable owner permission.
  const canDelete =
    canManageSupport && Boolean(request) && (request?.reporterUserId === userId || canManageTeamPermissions);
  const syncToLinear = useSyncRequestToLinear();
  const canSyncLinear = canManageSupport && canManageTeamPermissions && Boolean(request) && !request?.linearIssueId;
  const { staff, staffByUserId } = useSupportStaff();
  const [commentBody, setCommentBody] = useState("");
  // name -> userId collected from the Mentions picker; on submit only names still
  // present in the body count (the author may have deleted a mention by hand).
  const [pickedMentions, setPickedMentions] = useState<Record<string, string>>({});
  // Attachments already uploaded for the comment being composed (TRE-1555) —
  // removable until submit; the storage upload itself is not rolled back on remove.
  const [pendingCommentAttachments, setPendingCommentAttachments] = useState<SupportRequestAttachment[]>([]);
  const [commentUploadCount, setCommentUploadCount] = useState(0);
  // Inline edit draft of an own comment — one at a time, keyed by request id so
  // switching the drawer to another request drops back to read mode (same pattern
  // as descriptionDraft, no reset effect).
  const [commentEditDraft, setCommentEditDraft] = useState<{
    requestId: string;
    commentId: string;
    body: string;
  } | null>(null);
  const commentEdit =
    commentEditDraft && request && commentEditDraft.requestId === request.id ? commentEditDraft : null;
  // Picking Done/Rejected in the status select pauses for the closing-note modal
  // (TRE-1765 #4); cancel = status stays unchanged.
  const [pendingStatusChange, setPendingStatusChange] = useState<PendingStatusChange | null>(null);
  // Description edit draft, keyed by request id — switching the drawer to another
  // request drops back to read mode without any reset effect (TRE-1558).
  const [descriptionDraft, setDescriptionDraft] = useState<{ requestId: string; value: string } | null>(null);
  // Uploads finished in the current Upload batch — persisted with ONE update once the
  // whole batch is done, so parallel per-file updates cannot overwrite each other.
  const uploadPendingRef = useRef<SupportRequestAttachment[]>([]);

  const attachments =
    request && Array.isArray(request.attachments) ? (request.attachments as unknown as SupportRequestAttachment[]) : [];

  const { options: tagOptions } = useTagOptions("supportRequestTags");
  const requestTags =
    request && Array.isArray(request.tags)
      ? (request.tags as unknown[]).filter((tag): tag is string => typeof tag === "string")
      : [];

  const { releases, isLoading: releasesLoading, isError: releasesError } = useAppReleases();
  const requestFixedInVersions = useMemo(() => parseFixedInVersions(request?.fixedInVersions), [request]);

  // Options grouped per platform (newest release on top within a group); values
  // already saved on the request but missing from the list (e.g. saved before the
  // release was recorded) stay selectable instead of rendering blank — same
  // fallback as the assignee picker.
  const releaseOptions = useMemo(() => {
    const known = new Set<string>();
    const byPlatform = new Map<string, { value: string; label: string }[]>();

    const push = (platform: string, version: string) => {
      const value = encodeRelease({ platform, version } as SupportRequestFixedInVersion);
      if (known.has(value)) {
        return;
      }
      known.add(value);
      const group = byPlatform.get(platform) ?? [];
      group.push({ value, label: releaseLabel(platform, version) });
      byPlatform.set(platform, group);
    };

    releases.forEach((release) => push(release.platform, release.version));
    requestFixedInVersions.forEach((entry) => push(entry.platform, entry.version));

    // Fixed platform order first, then any unknown platforms found on requests.
    const orderedPlatforms = [
      ...Object.keys(APP_RELEASE_PLATFORM_LABELS),
      ...[...byPlatform.keys()].filter((platform) => !isKnownReleasePlatform(platform)),
    ];

    return orderedPlatforms
      .filter((platform) => byPlatform.has(platform))
      .map((platform) => ({
        label: isKnownReleasePlatform(platform) ? APP_RELEASE_PLATFORM_LABELS[platform] : platform,
        options: byPlatform.get(platform)!,
      }));
  }, [releases, requestFixedInVersions]);

  // One shared mutation serves the assignee/reporter selects, the attachment button
  // and the description editor — scope the spinners to the field actually being
  // updated, otherwise changing the assignee spins the "Dodaj załącznik" button.
  // (Tags don't need a spinner — they update optimistically.)
  const isUpdatingField = (field: "assigneeUserId" | "reporterUserId" | "traineeId" | "attachments" | "description") =>
    updateRequest.isLoading && updateRequest.variables?.[field] !== undefined;

  const {
    options: traineeOptions,
    isSearching: isSearchingTrainees,
    setSearchValue: setTraineeSearchValue,
  } = useTraineeSearch({
    enabled: canEditClient && Boolean(request),
    extraOption: request?.trainee
      ? {
          value: request.trainee.id,
          label: traineeLabel(request.trainee),
          shortLabel: traineeName(request.trainee),
          metadata: { email: request.trainee.email },
        }
      : null,
  });

  const isSavingClient = isUpdatingField("traineeId");

  const triage = (request?.triage as SupportRequestTriage | null) || null;
  const requestMetadata = ((request?.metadata as SupportRequestMetadata | null) || {}) as SupportRequestMetadata;

  const staffSelectOptions = useMemo(
    () =>
      staff.map((member) => ({
        value: member.userId,
        name: member.name,
        label: (
          <span className="flex items-center gap-2">
            <Avatar size={18} src={member.avatarUrl || undefined}>
              {member.name.charAt(0)}
            </Avatar>
            {member.name}
          </span>
        ),
      })),
    [staff],
  );

  // An assignee who has since left the board roles would render as a blank value —
  // keep them selectable/visible until someone reassigns.
  const assigneeOptions = useMemo(() => {
    if (request?.assignee && !staffByUserId.has(request.assignee.id)) {
      return [
        ...staffSelectOptions,
        {
          value: request.assignee.id,
          name: formatPersonName(request.assignee) || request.assignee.id,
          label: <span>{formatPersonName(request.assignee)}</span>,
        },
      ];
    }

    return staffSelectOptions;
  }, [staffSelectOptions, staffByUserId, request?.assignee]);

  const canEditReporter = canManageSupport && canManageTeamPermissions;
  const reporterOptions = useMemo(() => {
    if (request?.reporter && request.reporterUserId && !staffByUserId.has(request.reporterUserId)) {
      return [
        ...staffSelectOptions,
        {
          value: request.reporterUserId,
          name: formatPersonName(request.reporter) || request.reporterUserId,
          label: <span>{formatPersonName(request.reporter)}</span>,
        },
      ];
    }

    return staffSelectOptions;
  }, [staffSelectOptions, staffByUserId, request?.reporter, request?.reporterUserId]);

  // Names picked in the composer that are still present in the body — drives both the
  // input highlight and the mentions payload on submit.
  const activeMentionNames = useMemo(
    () => Object.keys(pickedMentions).filter((name) => commentBody.includes(`@${name}`)),
    [pickedMentions, commentBody],
  );

  const mentionNamesByComment = (comment: SupportCommentWithAuthor) =>
    (comment.mentions || []).map((id) => staffByUserId.get(id)?.name).filter((name): name is string => Boolean(name));

  const activeDescriptionDraft =
    request && descriptionDraft && descriptionDraft.requestId === request.id ? descriptionDraft : null;

  const handleSaveDescription = () => {
    if (!canManageSupport || !request || !activeDescriptionDraft) {
      return;
    }
    // Whitespace-only clears the description; an unchanged value just closes the
    // editor. On error the draft stays open for retry.
    const description = activeDescriptionDraft.value.trim() || null;

    if (description === (request.description?.trim() || null)) {
      setDescriptionDraft(null);
      return;
    }

    updateRequest.mutate(
      { id: request.id, description },
      {
        onSuccess: () => setDescriptionDraft(null),
      },
    );
  };

  const handleAddComment = () => {
    if (!canManageSupport) {
      return;
    }

    const body = commentBody.trim();
    // Attachment-only comments (e.g. just a screenshot) are allowed.
    if (!body && !pendingCommentAttachments.length) {
      return;
    }
    const mentions = activeMentionNames.map((name) => pickedMentions[name]).filter((id): id is string => Boolean(id));

    addComment.mutate(
      { body, mentions, attachments: pendingCommentAttachments },
      {
        onSuccess: () => {
          setCommentBody("");
          setPickedMentions({});
          setPendingCommentAttachments([]);
        },
      },
    );
  };

  return (
    <Drawer
      open={Boolean(request)}
      onClose={onClose}
      width={610}
      title={
        request ? (
          <span>
            <span className="mr-1.5 font-normal text-gray-400">#{request.displayId}</span>
            {request.title}
          </span>
        ) : undefined
      }
      extra={
        canDelete && request ? (
          <Popconfirm
            title="Usunąć zgłoszenie?"
            description="Zgłoszenie i komentarze zostaną usunięte bezpowrotnie."
            okButtonProps={{ danger: true }}
            onConfirm={() =>
              deleteRequest.mutate(
                { id: request.id },
                {
                  onSuccess: onClose,
                },
              )
            }
          >
            <Button danger type="text" size="small" icon={<DeleteOutlined />} loading={deleteRequest.isLoading}>
              Usuń
            </Button>
          </Popconfirm>
        ) : undefined
      }
    >
      {request && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Typography.Text type="secondary">Status</Typography.Text>
              <Select
                value={request.status as SupportRequestStatus}
                disabled={!canManageSupport}
                onChange={(status) => {
                  if (CLOSED_SUPPORT_STATUSES.includes(status)) {
                    setPendingStatusChange({ requestId: request.id, displayId: request.displayId, status });
                    return;
                  }
                  updateStatus.mutate({ id: request.id, status });
                }}
                loading={updateStatus.isLoading}
                className="w-48"
                options={BOARD_COLUMNS.map((status) => ({
                  value: status,
                  label: (
                    <span className="flex items-center gap-2">
                      <Badge color={STATUS_BADGE_COLORS[status]} />
                      {STATUS_LABELS[status]}
                    </span>
                  ),
                }))}
              />
            </div>
            <div className="flex items-center gap-2">
              {requestMetadata.slackPermalink && (
                <Button
                  icon={<SlackOutlined />}
                  href={requestMetadata.slackPermalink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Wątek
                </Button>
              )}
              {request.linearIssueUrl && (
                <Button icon={<LinkOutlined />} href={request.linearIssueUrl} target="_blank" rel="noopener noreferrer">
                  {request.linearIssueIdentifier || "Linear"}
                </Button>
              )}
              {canSyncLinear && (
                <Button
                  icon={<LinkOutlined />}
                  loading={syncToLinear.isLoading}
                  onClick={() => syncToLinear.mutate({ id: request.id })}
                >
                  Utwórz w Linear
                </Button>
              )}
            </div>
          </div>

          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Zgłosił(a)">
              {canEditReporter ? (
                <Select
                  showSearch
                  optionFilterProp="name"
                  placeholder="Wybierz zgłaszającego"
                  value={request.reporterUserId}
                  onChange={(value) => updateRequest.mutate({ id: request.id, reporterUserId: value })}
                  loading={isUpdatingField("reporterUserId")}
                  options={reporterOptions}
                  className="min-w-52"
                />
              ) : (
                formatPersonName(request.reporter) || "—"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Klient">
              {canEditClient ? (
                <div className="flex w-full flex-col items-start gap-1">
                  <Select
                    showSearch
                    allowClear
                    filterOption={false}
                    placeholder="Szukaj po imieniu, nazwisku lub emailu"
                    value={request.traineeId}
                    onSearch={setTraineeSearchValue}
                    // Clearing unlinks the email too — leaving it behind would keep
                    // the ticket pointing at a client the field says it has none.
                    onChange={(value?: string) =>
                      updateRequest.mutate({
                        id: request.id,
                        traineeId: value ?? null,
                        clientEmail: value ? undefined : null,
                      })
                    }
                    loading={isSearchingTrainees || isSavingClient}
                    disabled={isSavingClient}
                    // Name only once picked; the dropdown keeps the email, which is
                    // what tells two same-named clients apart.
                    optionLabelProp="shortLabel"
                    options={traineeOptions}
                    notFoundContent="Brak wyników wyszukiwania"
                    className="w-full"
                  />
                  {isSavingClient ? (
                    <Typography.Text type="secondary" className="text-xs">
                      Zapisuję...
                    </Typography.Text>
                  ) : // The relation is refetched after the write, so until it catches up with
                  // the (optimistic) id the link would still open the previous client.
                  request.trainee && request.trainee.id === request.traineeId ? (
                    <Link to={`/trainee/details/${request.trainee.id}`} className="text-xs">
                      Otwórz profil klientki
                    </Link>
                  ) : (
                    request.clientEmail && (
                      <Typography.Text type="secondary" className="text-xs">
                        {request.clientEmail}
                      </Typography.Text>
                    )
                  )}
                </div>
              ) : request.trainee ? (
                <Link to={`/trainee/details/${request.trainee.id}`}>{traineeLabel(request.trainee)}</Link>
              ) : (
                request.clientEmail || "—"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Źródło">{SOURCE_LABELS[request.source] || request.source}</Descriptions.Item>
            <Descriptions.Item label="Utworzono">
              {dayjs(request.createdAt).format("DD.MM.YYYY HH:mm")}
            </Descriptions.Item>
            <Descriptions.Item label="Przypisany">
              <div className="flex items-center gap-1">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="name"
                  placeholder="Nieprzypisane"
                  value={request.assigneeUserId}
                  disabled={!canManageSupport}
                  onChange={(value) => updateRequest.mutate({ id: request.id, assigneeUserId: value ?? null })}
                  loading={isUpdatingField("assigneeUserId")}
                  options={assigneeOptions}
                  className="min-w-52 flex-1"
                />
                {canManageSupport && request.assigneeUserId !== userId && userId && staffByUserId.has(userId) && (
                  <Button
                    type="link"
                    size="small"
                    className="px-1"
                    onClick={() => updateRequest.mutate({ id: request.id, assigneeUserId: userId })}
                  >
                    Przypisz do mnie
                  </Button>
                )}
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Termin">
              <DatePicker
                allowClear
                disabled={!canManageSupport}
                format="DD.MM.YYYY"
                placeholder="Brak terminu"
                value={request.dueDate ? dayjs(request.dueDate) : null}
                onChange={(date) =>
                  updateRequest.mutate({ id: request.id, dueDate: date ? date.format("YYYY-MM-DD") : null })
                }
              />
            </Descriptions.Item>
            <Descriptions.Item label="Tagi">
              <Select
                mode="multiple"
                allowClear
                disabled={!canManageSupport}
                placeholder="Brak tagów"
                value={requestTags}
                // Coalesced — the cache updates on every click (the select reads from
                // it), the server gets one debounced full-replacement write.
                onChange={(tags: string[]) => queueFieldUpdate(request.id, { tags })}
                options={tagOptions}
                notFoundContent="Brak zdefiniowanych tagów"
                className="min-w-52 w-full"
              />
            </Descriptions.Item>
            <Descriptions.Item label="Poprawione w wersji">
              <Select
                mode="multiple"
                allowClear
                showSearch
                disabled={!canManageSupport}
                optionFilterProp="label"
                placeholder="Wybierz wydania"
                value={requestFixedInVersions.map(encodeRelease)}
                onChange={(values: string[]) =>
                  queueFieldUpdate(request.id, { fixedInVersions: values.map(decodeRelease) })
                }
                // Coalesced like tags — the only spinner-worthy wait is the options fetch.
                loading={releasesLoading}
                options={releaseOptions}
                notFoundContent={
                  releasesLoading
                    ? "Ładowanie wydań..."
                    : releasesError
                      ? "Nie udało się pobrać listy wydań"
                      : "Brak wydań — zapisz wersje w konfiguracji aplikacji"
                }
                className="min-w-52 w-full"
              />
            </Descriptions.Item>
          </Descriptions>

          {triage && (
            <Alert
              type={triage.resolution ? "success" : "info"}
              icon={<RobotOutlined />}
              showIcon
              message={
                <span className="flex items-center gap-2">
                  Agent — propozycja:
                  <Tag color={STATUS_COLORS[triage.proposedStatus]} className="m-0">
                    {STATUS_LABELS[triage.proposedStatus]}
                  </Tag>
                  <span className="text-xs text-gray-400">{dayjs(triage.analyzedAt).format("DD.MM.YYYY HH:mm")}</span>
                </span>
              }
              description={
                <div className="space-y-2">
                  <Typography.Paragraph className="mb-0 whitespace-pre-wrap">{triage.summary}</Typography.Paragraph>
                  {(triage.questions?.length ?? 0) > 0 && (
                    <ul className="mb-0 list-disc pl-5">
                      {triage.questions?.map((question, index) => (
                        <li key={index}>{question}</li>
                      ))}
                    </ul>
                  )}
                  {triage.resolution ? (
                    <Typography.Text type="secondary" className="text-xs">
                      {triage.resolution.outcome === "approved" ? "Zatwierdzona" : "Odrzucona"}{" "}
                      {dayjs(triage.resolution.at).format("DD.MM.YYYY HH:mm")}
                    </Typography.Text>
                  ) : canManageSupport ? (
                    <div className="flex gap-2">
                      <Button
                        type="primary"
                        size="small"
                        icon={<CheckOutlined />}
                        loading={resolveTriage.isLoading}
                        onClick={() => resolveTriage.mutate({ id: request.id, outcome: "approved" })}
                      >
                        Zatwierdź
                      </Button>
                      <Button
                        size="small"
                        icon={<CloseOutlined />}
                        loading={resolveTriage.isLoading}
                        onClick={() => resolveTriage.mutate({ id: request.id, outcome: "rejected" })}
                      >
                        Odrzuć
                      </Button>
                    </div>
                  ) : null}
                </div>
              }
            />
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Typography.Text strong>Opis</Typography.Text>
              {canManageSupport && !activeDescriptionDraft && (
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => setDescriptionDraft({ requestId: request.id, value: request.description || "" })}
                >
                  {request.description ? "Edytuj" : "Dodaj opis"}
                </Button>
              )}
            </div>
            {canManageSupport && activeDescriptionDraft ? (
              <div className="space-y-2">
                <Input.TextArea
                  autoSize={{ minRows: 3, maxRows: 12 }}
                  maxLength={20000}
                  autoFocus
                  disabled={isUpdatingField("description")}
                  placeholder="Opisz zgłoszenie..."
                  value={activeDescriptionDraft.value}
                  onChange={(event) => setDescriptionDraft({ requestId: request.id, value: event.target.value })}
                />
                <div className="flex justify-end gap-2">
                  <Button
                    size="small"
                    disabled={isUpdatingField("description")}
                    onClick={() => setDescriptionDraft(null)}
                  >
                    Anuluj
                  </Button>
                  <Button
                    type="primary"
                    size="small"
                    loading={isUpdatingField("description")}
                    onClick={handleSaveDescription}
                  >
                    Zapisz
                  </Button>
                </div>
              </div>
            ) : request.description ? (
              <Typography.Paragraph className="mb-0 whitespace-pre-wrap rounded-md bg-gray-50 p-3">
                {request.description}
              </Typography.Paragraph>
            ) : (
              <Typography.Text type="secondary">Brak opisu</Typography.Text>
            )}
          </div>

          <div className="space-y-2">
            <Typography.Text strong className="block">
              Załączniki
            </Typography.Text>
            <SupportAttachmentList attachments={attachments} />
            {canManageSupport && (
              <Upload
                // The wrapper span is inline by default, so the space-y-2 margin-top
                // between the thumbnail list and this button would be ignored.
                className="block"
                multiple
                showUploadList={false}
                customRequest={async ({ file, onSuccess, onError }) => {
                  try {
                    const attachment = await uploadSupportAttachment(file as File);
                    uploadPendingRef.current.push(attachment);
                    onSuccess?.(attachment.url);
                  } catch (error) {
                    message.error(error instanceof Error ? error.message : "Błąd wysyłania pliku");
                    onError?.(error as Error);
                  }
                }}
                onChange={({ fileList }) => {
                  const allSettled = fileList.every((file) => file.status === "done" || file.status === "error");
                  if (allSettled && uploadPendingRef.current.length) {
                    const uploaded = uploadPendingRef.current;
                    uploadPendingRef.current = [];
                    updateRequest.mutate({ id: request.id, attachments: [...attachments, ...uploaded] });
                  }
                }}
              >
                <Button size="small" icon={<PaperClipOutlined />} loading={isUpdatingField("attachments")}>
                  Dodaj załącznik
                </Button>
              </Upload>
            )}
          </div>

          <Divider className="my-2">Komentarze</Divider>

          {commentsLoading ? (
            <div className="flex justify-center py-4">
              <Spin />
            </div>
          ) : comments.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Brak komentarzy" />
          ) : (
            <List
              dataSource={comments}
              renderItem={(comment) => {
                const isOwnComment =
                  canManageSupport &&
                  comment.authorType !== SupportRequestCommentAuthorType.Agent &&
                  comment.authorUserId === userId;
                const isEditingThisComment = canManageSupport && commentEdit?.commentId === comment.id;
                const commentAttachments = Array.isArray(comment.attachments)
                  ? (comment.attachments as unknown as SupportRequestAttachment[])
                  : [];
                const statusChange = (comment.metadata as SupportRequestCommentMetadata | null)?.statusChange;
                // updatedAt is touched only by the edit mutation; the insert writes
                // both timestamps in one statement, hence the small tolerance.
                const wasEdited = dayjs(comment.updatedAt).diff(comment.createdAt, "second") > 5;

                return (
                  <List.Item className="!px-0">
                    <div className="w-full space-y-1">
                      <div className="flex items-center justify-between text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          {comment.authorType === SupportRequestCommentAuthorType.Agent && <RobotOutlined />}
                          {comment.authorType === SupportRequestCommentAuthorType.Agent
                            ? "Agent"
                            : formatPersonName(comment.author) || "—"}
                          {statusChange && (
                            <Tag color={STATUS_COLORS[statusChange.to] || "default"} className="m-0">
                              → {STATUS_LABELS[statusChange.to] || statusChange.to}
                            </Tag>
                          )}
                          {wasEdited && <span className="text-gray-300">(edytowano)</span>}
                        </span>
                        <span className="flex items-center gap-1">
                          {dayjs(comment.createdAt).format("DD.MM.YYYY HH:mm")}
                          {isOwnComment && !isEditingThisComment && (
                            <>
                              <Button
                                size="small"
                                type="text"
                                icon={<EditOutlined />}
                                title="Edytuj komentarz"
                                onClick={() =>
                                  setCommentEditDraft({
                                    requestId: request.id,
                                    commentId: comment.id,
                                    body: comment.body,
                                  })
                                }
                              />
                              <Popconfirm
                                title="Usunąć komentarz?"
                                okText="Usuń"
                                cancelText="Anuluj"
                                okButtonProps={{ danger: true, loading: deleteComment.isLoading }}
                                onConfirm={() => deleteComment.mutate({ commentId: comment.id })}
                              >
                                <Button
                                  size="small"
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  title="Usuń komentarz"
                                />
                              </Popconfirm>
                            </>
                          )}
                        </span>
                      </div>
                      {isEditingThisComment ? (
                        <div className="space-y-2">
                          <Input.TextArea
                            autoSize={{ minRows: 2, maxRows: 6 }}
                            value={commentEdit.body}
                            onChange={(event) =>
                              setCommentEditDraft({
                                requestId: request.id,
                                commentId: comment.id,
                                body: event.target.value,
                              })
                            }
                          />
                          <div className="flex justify-end gap-2">
                            <Button size="small" icon={<CloseOutlined />} onClick={() => setCommentEditDraft(null)}>
                              Anuluj
                            </Button>
                            <Button
                              size="small"
                              type="primary"
                              icon={<CheckOutlined />}
                              loading={updateComment.isLoading}
                              // Attachment-only comments may have an empty body — mirrors add.
                              disabled={!commentEdit.body.trim() && commentAttachments.length === 0}
                              onClick={() =>
                                updateComment.mutate(
                                  { commentId: comment.id, body: commentEdit.body.trim() },
                                  { onSuccess: () => setCommentEditDraft(null) },
                                )
                              }
                            >
                              Zapisz
                            </Button>
                          </div>
                        </div>
                      ) : (
                        comment.body && <CommentBody comment={comment} mentionNames={mentionNamesByComment(comment)} />
                      )}
                      {commentAttachments.length > 0 && (
                        <SupportAttachmentList attachments={commentAttachments} thumbnailSize={64} />
                      )}
                    </div>
                  </List.Item>
                );
              }}
            />
          )}

          {canManageSupport && (
            <div className="space-y-2">
              {(pendingCommentAttachments.length > 0 || commentUploadCount > 0) && (
                <div className="flex flex-wrap items-center gap-2">
                  {pendingCommentAttachments.map((attachment, index) =>
                    isImageAttachment(attachment) ? (
                      <div key={`${attachment.url}-${index}`} className="relative">
                        <HeicImage
                          src={attachment.url}
                          alt={attachment.name}
                          title={attachment.name}
                          width={48}
                          height={48}
                          className="rounded-md border border-gray-200 object-cover"
                        />
                        <Button
                          size="small"
                          shape="circle"
                          icon={<CloseOutlined className="text-[10px]" />}
                          title="Usuń załącznik"
                          className="absolute -right-2 -top-2 h-5 w-5 min-w-0 shadow"
                          onClick={() =>
                            setPendingCommentAttachments((previous) =>
                              previous.filter((_, attachmentIndex) => attachmentIndex !== index),
                            )
                          }
                        />
                      </div>
                    ) : (
                      <Tag
                        key={`${attachment.url}-${index}`}
                        closable
                        icon={<PaperClipOutlined />}
                        className="m-0"
                        onClose={(event) => {
                          // The Tag is state-driven — stop antd's own remove animation
                          // from racing the re-render.
                          event.preventDefault();
                          setPendingCommentAttachments((previous) =>
                            previous.filter((_, attachmentIndex) => attachmentIndex !== index),
                          );
                        }}
                      >
                        {attachment.name}
                      </Tag>
                    ),
                  )}
                  {commentUploadCount > 0 && <Spin size="small" />}
                </div>
              )}
              <div className="flex gap-2">
                <div className="relative flex-1 rounded-lg bg-white">
                  <MentionsHighlightOverlay body={commentBody} names={activeMentionNames} />
                  <Mentions
                    autoSize={{ minRows: 2, maxRows: 6 }}
                    className="relative bg-transparent [&_textarea]:bg-transparent"
                    value={commentBody}
                    onChange={(text) => setCommentBody(text)}
                    onSelect={(option) => {
                      if (option.value && option.key) {
                        setPickedMentions((previous) => ({ ...previous, [String(option.value)]: String(option.key) }));
                      }
                    }}
                    placeholder="Dodaj komentarz... (@ aby wspomnieć)"
                    options={staff.map((member) => ({
                      value: member.name,
                      key: member.userId,
                      label: (
                        <span className="flex items-center gap-2">
                          <Avatar size={18} src={member.avatarUrl || undefined}>
                            {member.name.charAt(0)}
                          </Avatar>
                          {member.name}
                        </span>
                      ),
                    }))}
                  />
                </div>
                <Upload
                  multiple
                  showUploadList={false}
                  beforeUpload={(file, batch) => {
                    // Mirrors the add-comment zod cap; batch.indexOf covers a single
                    // multi-select exceeding the limit before any state updates land.
                    if (
                      pendingCommentAttachments.length + commentUploadCount + batch.indexOf(file) >=
                      MAX_COMMENT_ATTACHMENTS
                    ) {
                      message.error({
                        content: `Maksymalnie ${MAX_COMMENT_ATTACHMENTS} załączników w komentarzu`,
                        key: "comment-attachment-limit",
                      });
                      return Upload.LIST_IGNORE;
                    }
                    return true;
                  }}
                  customRequest={async ({ file, onSuccess, onError }) => {
                    setCommentUploadCount((count) => count + 1);
                    try {
                      const attachment = await uploadSupportAttachment(file as File);
                      setPendingCommentAttachments((previous) => [...previous, attachment]);
                      onSuccess?.(attachment.url);
                    } catch (error) {
                      message.error(error instanceof Error ? error.message : "Błąd wysyłania pliku");
                      onError?.(error as Error);
                    } finally {
                      setCommentUploadCount((count) => count - 1);
                    }
                  }}
                >
                  <Button icon={<PaperClipOutlined />} title="Dodaj załącznik do komentarza" />
                </Upload>
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={handleAddComment}
                  loading={addComment.isLoading}
                  disabled={(!commentBody.trim() && !pendingCommentAttachments.length) || commentUploadCount > 0}
                />
              </div>
            </div>
          )}

          <StatusChangeCommentModal
            pending={pendingStatusChange}
            onCancel={() => setPendingStatusChange(null)}
            onSubmit={(comment) => {
              if (pendingStatusChange) {
                updateStatus.mutate({
                  id: pendingStatusChange.requestId,
                  status: pendingStatusChange.status,
                  comment,
                });
              }
              setPendingStatusChange(null);
            }}
          />
        </div>
      )}
    </Drawer>
  );
};
